import * as React from 'react';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  Callout,
  FilterChip,
  WorkspaceButton,
  WorkspaceField,
  WorkspaceInput,
  WorkspaceSelect,
  useToast,
  workspaceInputClass,
} from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import { useDiscoveryDocumentsQuery, type AreaCoverage } from '@/lib/api/discovery';
import { useAddDocumentEvidence, usePassageQuery } from '@/lib/api/discovery-findings';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import { cx } from '@/lib/utils/cn';
import { coverageFlipText } from './findings-logic';

/**
 * Add a passage from a document as evidence (E12-F07, Decision 54.25),
 * in place under the area's status. A document is one independent
 * source; the passage is cited by page (or by sheet and rows for a data
 * extract), pre-filled from the parsed page, and confirmed at once. A
 * passage not found on the cited page is saved with a warning, never
 * refused. Prototype: `inlineForm` (kind `evidence`).
 */

const MAX_PASSAGE = 4000;
const DATA_MIME = /(spreadsheetml|ms-excel|text\/csv)/i;

const positive = (v: string): number | null => {
  const n = Number(v.trim());
  return Number.isInteger(n) && n > 0 ? n : null;
};

export function DocumentEvidenceForm({
  engagementId,
  areaId,
  areas,
  onDone,
}: {
  engagementId: string;
  areaId: string;
  areas: ReadonlyArray<AreaCoverage>;
  onDone: () => void;
}) {
  const docs = useDiscoveryDocumentsQuery(engagementId, true);
  const add = useAddDocumentEvidence(engagementId);
  const { toast } = useToast();
  const [docId, setDocId] = React.useState('');
  const [page, setPage] = React.useState('1');
  const [sheet, setSheet] = React.useState('');
  const [rows, setRows] = React.useState({ from: '', to: '' });
  /** null until the consultant types: the passage follows the parsed page. */
  const [typed, setTyped] = React.useState<string | null>(null);
  const [areaIds, setAreaIds] = React.useState<Set<string>>(() => new Set([areaId]));
  const [error, setError] = React.useState<string | null>(null);

  // An interview transcript is cited through its interview, never as a
  // document (the server refuses it), so it is not offered here (T-0192).
  const documents = React.useMemo(
    () => (docs.data?.documents ?? []).filter((x) => (x.purpose ?? 'general') === 'general'),
    [docs.data],
  );
  const onlyTranscripts = (docs.data?.documents.length ?? 0) > 0 && documents.length === 0;
  const doc = documents.find((x) => x.id === docId) ?? documents[0];
  const isData = Boolean(doc?.mime_type && DATA_MIME.test(doc.mime_type));
  const pageNo = isData ? null : positive(page);
  const passage = usePassageQuery(engagementId, doc?.id ?? '', pageNo);
  const text = typed ?? passage.data?.text ?? '';
  const rowFrom = positive(rows.from);
  const rowTo = rows.to.trim() ? positive(rows.to) : rowFrom;

  const why = !doc
    ? 'Pick a document'
    : isData
      ? !sheet.trim() || !rowFrom || !rowTo || rowTo < rowFrom
        ? 'Give the sheet and rows'
        : null
      : !pageNo
        ? 'Give the page'
        : null;
  const problem =
    why ??
    (!text.trim()
      ? 'Add the passage'
      : text.length > MAX_PASSAGE
        ? `Keep the passage under ${MAX_PASSAGE.toLocaleString()} characters`
        : areaIds.size === 0
          ? 'Pick at least one area'
          : null);

  const submit = () => {
    if (problem || !doc) return;
    setError(null);
    add.mutate(
      {
        body: {
          document_id: doc.id,
          ...(isData
            ? { sheet: sheet.trim(), row_from: rowFrom!, row_to: rowTo! }
            : { page: pageNo! }),
          text: text.trim(),
          area_ids: [...areaIds],
        },
        key: newIdempotencyKey(),
      },
      {
        onSuccess: (r) => {
          const where = isData ? `${sheet.trim()}, rows ${rowFrom}–${rowTo}` : `p. ${pageNo}`;
          const flip = coverageFlipText(r.coverage_changes);
          const warn =
            r.warning === 'passage_not_found_on_page'
              ? ` The passage was not found on ${isData ? 'those rows' : `page ${pageNo}`} as parsed: check the citation.`
              : r.warning === 'passage_not_checked'
                ? ' The page has no parsed text, so the passage could not be checked against it.'
                : '';
          toast({
            message: `${r.number} added from ${doc.name}, ${where}.${flip ? ` ${flip}` : ''}${warn}`,
            ...(warn ? { durationMs: null } : {}),
          });
          onDone();
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not add the evidence.'),
      },
    );
  };

  const toggleArea = (id: string) =>
    setAreaIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section aria-label="Add evidence from a document">
      <Callout tone="form">
        <h3 className="text-body-sm font-semibold text-ink-900">Add evidence from a document</h3>
        <p className="mb-2.5 text-meta text-ink-500">
          A document is an independent source. The passage is cited by page, like every citation in
          Caliber.
        </p>
        <div className="flex flex-col gap-3">
          {docs.error ? <ErrorMessage error={docs.error} /> : null}
          {docs.data && documents.length === 0 ? (
            <p className="text-body-sm text-ink-500">
              {onlyTranscripts
                ? 'The only documents here are interview transcripts, and those are cited from their interview. Upload a document in Documents, then cite it here.'
                : 'This engagement has no documents yet. Upload one in Documents, then cite it here.'}
            </p>
          ) : null}
          <div className="flex flex-wrap items-end gap-3">
            <WorkspaceField label="Document" className="min-w-[240px] flex-1">
              {(id) => (
                <WorkspaceSelect
                  id={id}
                  value={doc?.id ?? ''}
                  onChange={(e) => {
                    setDocId(e.target.value);
                    setTyped(null);
                  }}
                >
                  {documents.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </WorkspaceSelect>
              )}
            </WorkspaceField>
            {isData ? (
              <>
                <WorkspaceField label="Sheet" className="w-[140px]">
                  {(id) => (
                    <WorkspaceInput
                      id={id}
                      value={sheet}
                      onChange={(e) => setSheet(e.target.value)}
                    />
                  )}
                </WorkspaceField>
                <WorkspaceField label="Rows" className="w-[150px]">
                  {(id) => (
                    <span className="flex items-center gap-1.5">
                      <WorkspaceInput
                        id={id}
                        aria-label="First row"
                        inputMode="numeric"
                        value={rows.from}
                        onChange={(e) => setRows((r) => ({ ...r, from: e.target.value }))}
                      />
                      <span className="text-ink-500">–</span>
                      <WorkspaceInput
                        aria-label="Last row"
                        inputMode="numeric"
                        value={rows.to}
                        onChange={(e) => setRows((r) => ({ ...r, to: e.target.value }))}
                      />
                    </span>
                  )}
                </WorkspaceField>
              </>
            ) : (
              <WorkspaceField label="Page" className="w-[110px]">
                {(id) => (
                  <WorkspaceInput
                    id={id}
                    inputMode="numeric"
                    value={page}
                    onChange={(e) => {
                      setPage(e.target.value);
                      setTyped(null);
                    }}
                  />
                )}
              </WorkspaceField>
            )}
          </div>
          <WorkspaceField
            label="Passage"
            help={
              passage.data?.text && typed === null
                ? 'Pre-filled from the parsed page. Trim it to the part that matters.'
                : 'Paste or type the passage from this page.'
            }
          >
            {(id, describedBy) => (
              <textarea
                id={id}
                aria-describedby={describedBy}
                rows={3}
                className={cx(workspaceInputClass, 'h-auto py-2')}
                value={text}
                onChange={(e) => setTyped(e.target.value)}
              />
            )}
          </WorkspaceField>
          <div>
            <p className="mb-1.5 text-meta font-medium text-ink-700">Counts toward</p>
            <div role="group" aria-label="Counts toward" className="flex flex-wrap gap-1.5">
              {areas.map((a) => (
                <FilterChip
                  key={a.area_id}
                  on={areaIds.has(a.area_id)}
                  onClick={() => toggleArea(a.area_id)}
                >
                  {a.label}
                </FilterChip>
              ))}
            </div>
          </div>
          {error ? (
            <p role="alert" className="text-meta text-posture-critical">
              {error}
            </p>
          ) : null}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2.5">
          {problem ? <span className="text-meta text-ink-500">{problem}.</span> : null}
          <WorkspaceButton variant="tertiary" size="md" onClick={onDone}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            variant="primary"
            size="md"
            disabled={Boolean(problem) || add.isPending}
            onClick={submit}
          >
            {add.isPending ? 'Adding…' : 'Add evidence'}
          </WorkspaceButton>
        </div>
      </Callout>
    </section>
  );
}
